import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { StationeryPriceInfoRawLineItem, StationeryProduct } from '@shared/utils/print-price/types';
import { useCallback } from 'react';
import { PriceSummaryLineItem } from '../PriceSummary.types';
import { PriceSummaryCurrencyFormatOptions } from '../ProductPriceSummary.controller';
import { calculateStationeryPricing } from '@shared/utils/print-price/utils/calculatePrices';
import { useProductAttributes } from '@apps/card/components/ProductUIConfig/useProductAttributes';

// Converts fancy line items to translated Label / Value pairs
export const usePriceCalculatorLineItemsToPriceSummaryLineItems = (product: StationeryProduct, quantity: number) => {
  const attributes = useProductAttributes();
  const formatPrice = useMinorUnitsToPriceString();

  const toLineItems = useCallback(
    (lineItems: StationeryPriceInfoRawLineItem[], stepFilter?: string[], includeQuantity?: boolean): PriceSummaryLineItem[] => {
      return lineItems
        .map(item => {
          switch (item.type) {
            case 'base_cost': {
              // Move to translation + interpolation
              // Replace 'cards' with some string on ProductConfig.
              // for cases like 'napkins,' 'signs,' etc...
              if (stepFilter && !includeQuantity) {
                return null;
              }

              const unitPriceFormatted = formatPrice(item.unitPriceInMinorUnits, PriceSummaryCurrencyFormatOptions);
              const totalPriceFormatted = formatPrice(item.totalPriceInMinorUnits, PriceSummaryCurrencyFormatOptions);
              return [`Quantity: ${item.quantity} items at ${unitPriceFormatted} each`, totalPriceFormatted];
            }
            case 'option': {
              const attribute = attributes.find(attr => attr.attributeId === item.attributeId);
              if (!attribute) return null;

              const option = attribute.options.find(opt => opt.optionId === item.optionId);
              const attributeName = attribute?.lineItem ?? '';
              const optionName = option?.lineItem ?? '';

              if (stepFilter && attribute?.step && !stepFilter.includes(attribute.step)) {
                return null;
              }

              const totalPriceFormatted = formatPrice(item.totalPriceInMinorUnits, PriceSummaryCurrencyFormatOptions);
              return [`${attributeName}${optionName ? `: ${optionName}` : ''}`, totalPriceFormatted];
            }
            case 'delivery': {
              if (stepFilter && !stepFilter.includes('shipping')) {
                return null;
              }

              const deliveryId = item.deliveryMethodId;
              const deliveryConfig = product.shippingOptions?.find(c => c.id === deliveryId);

              if (!deliveryConfig) {
                throw new Error('Invalid delivery config for line item.');
              }

              const deliveryPrice = calculateStationeryPricing(deliveryConfig.pricing, quantity);
              const deliveryPriceFormatted = formatPrice(deliveryPrice, PriceSummaryCurrencyFormatOptions);

              return [`Shipping: ${deliveryConfig.label}`, deliveryPriceFormatted, { highlight: deliveryPrice === 0 }];
            }
            case 'tax': {
              if (stepFilter && !stepFilter.includes('salesTax')) {
                return null;
              }

              const taxFormatted = formatPrice(item.totalPriceInMinorUnits, PriceSummaryCurrencyFormatOptions);
              return ['Sales Tax', taxFormatted];
            }
          }
        })
        .filter(lineItem => lineItem !== null) as PriceSummaryLineItem[];
    },
    [attributes, formatPrice, product.shippingOptions, quantity],
  );

  return toLineItems;
};
